import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import { ColorCategoryDictionary, ExplanationsDictionary, MenuSubcategoryDictionary } from "../../../models/categories";
import { NEW_BLUE_COLOR } from "../../../theme/theme";
import { useTranslation } from "react-i18next";
import { NutritionContext } from "../context/nutrition-context";
import { ExplanationContext } from "./context/explanation-context";
import { Explanation } from "../../../models/types/category-types";
import { PresentationNavButton } from "../presentation/components/presentation-nav-button";
import { ArrowLeftIcon } from "../../../assets/icons/arrows/arrow-left-icon";
import { ArrowRightIcon } from "../../../assets/icons/arrows/arrow-right-icon";
import { PageIndicator } from "../../../components/core/page-indicator";

const useStyles = makeStyles((theme: Theme) => ({
  hero: {
    width: "100%",
  },
  noButton: {
    backgroundColor: `${theme.palette.grey[500]} !important`,
  },
  button: {
    width: theme.spacing(13),
    height: theme.spacing(13),
    margin: `0 ${theme.spacing(2)}px`,
    backgroundColor: NEW_BLUE_COLOR,
  },
  footer: {
    paddingTop: theme.spacing(4),
  },
  explanationFooter: {
    paddingTop: theme.spacing(10),
  },
  explanationButton: {
    marginTop: theme.spacing(4),
    color: theme.palette.common.white,
    width: 240,
  },
  previousButton: {
    marginRight: theme.spacing(2),
  },
  checkboxLabel: {
    fontWeight: 300,
  },
  navButtons: {
    order: 1,
    [theme.breakpoints.only("xs")]: { order: 2 },
  },
  dontShowAgainCheckbox: {
    order: 2,
    [theme.breakpoints.only("xs")]: {
      paddingBottom: theme.spacing(3),
      order: 1,
    },
  },
  pageIndicator: {
    order: 3,
  },
}));

interface IExplanationWrapperProps {
  explanation?: Explanation;
  pages: number;
  activeIndex: number;
  onPrevious: () => void;
  setIndex: (index: number) => void;
}

export const ExplanationWrapper: FunctionComponent<IExplanationWrapperProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { pages, activeIndex, setIndex, onPrevious } = props;
  const { activeCategory, activeSubcategory, activeIndex: activePageIndex } = useContext(NutritionContext);
  const { isExplanationDismissed, setExplanationAsDismissed } = useContext(ExplanationContext);
  const [dontShowAgainChecked, setDontShowAgainChecked] = useState<boolean>(false);
  const [dismissExplanation, setDismissExplanation] = useState<boolean>(false);
  const [explanationIndex, setExplanationIndex] = useState<number>(0);
  const handleChange = useCallback(() => {
    setDontShowAgainChecked(!dontShowAgainChecked);
  }, [dontShowAgainChecked]);
  const subcategory = useMemo(() => MenuSubcategoryDictionary[activeSubcategory], [activeSubcategory]);

  const explanationKey = useMemo(() => {
    return subcategory.explanations
      ? subcategory.explanations[activePageIndex - 1]
      : props.explanation
      ? props.explanation
      : undefined;
  }, [subcategory.explanations, props.explanation, activePageIndex]);

  const ExplanationComponent = useMemo(() => {
    if (explanationKey) {
      return ExplanationsDictionary[explanationKey][explanationIndex];
    }

    return undefined;
  }, [explanationKey, explanationIndex]);

  const handleExplanationDismiss = useCallback(() => {
    if (
      dontShowAgainChecked &&
      explanationKey &&
      explanationIndex >= ExplanationsDictionary[explanationKey].length - 1
    ) {
      setExplanationAsDismissed(explanationKey);
    }

    if (explanationKey && ExplanationsDictionary[explanationKey].length > 0) {
      setExplanationIndex((prevState: number) => prevState + 1);
      if (ExplanationsDictionary[explanationKey].length === explanationIndex + 1) {
        setDismissExplanation(true);
      }
    } else {
      setDismissExplanation(true);
    }
  }, [dontShowAgainChecked, setExplanationAsDismissed, explanationKey, explanationIndex]);

  const handlePrevious = useCallback(() => {
    if (explanationIndex !== 0 && explanationKey && ExplanationsDictionary[explanationKey].length > 0) {
      setExplanationIndex((prevState: number) => prevState - 1);
    } else {
      onPrevious();
    }
  }, [explanationIndex, setExplanationIndex, explanationKey, onPrevious]);

  const showExplanation = useMemo(
    () => explanationKey && !dismissExplanation && !isExplanationDismissed(explanationKey) && !!ExplanationComponent,
    [explanationKey, dismissExplanation, isExplanationDismissed, ExplanationComponent],
  );

  useEffect(() => {
    setDismissExplanation(false);
    setExplanationIndex(0);
  }, [subcategory]);

  return (
    <>
      {showExplanation && ExplanationComponent && (
        <Grid container>
          <Grid item container direction="row" className={classes.hero}>
            <ExplanationComponent />
          </Grid>

          <Grid item container justify="space-between" alignItems="center" className={classes.explanationFooter}>
            <Grid item className={classes.navButtons}>
              <Grid container>
                <Grid item className={classes.previousButton}>
                  <PresentationNavButton
                    backgroundColor={ColorCategoryDictionary[activeCategory].color}
                    onClick={handlePrevious}
                    disabled={activeIndex === 0}
                  >
                    <ArrowLeftIcon width={24} />
                  </PresentationNavButton>
                </Grid>
                <Grid item>
                  <PresentationNavButton
                    backgroundColor={ColorCategoryDictionary[activeCategory].color}
                    onClick={handleExplanationDismiss}
                  >
                    <ArrowRightIcon width={24} />
                  </PresentationNavButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={"auto"} className={classes.dontShowAgainCheckbox}>
              <FormControlLabel
                control={
                  <Checkbox checked={dontShowAgainChecked} onChange={handleChange} name="checkedB" color="primary" />
                }
                label={<Typography className={classes.checkboxLabel}>{t("general.dont_show_this_again")}</Typography>}
              />
            </Grid>
            <Grid item className={classes.pageIndicator} style={{ alignItems: "flex-end" }}>
              <PageIndicator
                pages={pages}
                activePage={activeIndex}
                colorHEX={ColorCategoryDictionary[activeCategory].color}
                onClickPageIndicator={setIndex}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {(!showExplanation || !ExplanationComponent) && props.children}
    </>
  );
};
